import { roundNumberAndDecimals } from '@teinor/erp/prices'
import { recalculateAllQuoteOrderRows, totalCalculateParams } from '@teinor/erp/prices/quote/calculate'
import { OrderTableData } from '@teinor/erp/prices/quote/totals'
import { HeaderDataCalculate, financialRowTypesInversed, financialRowCalculationTypesInversed } from '@teinor/erp/types/company/order/general'
import { useCallback, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { CalculateParamsTy } from '../../../../../../../../../baseComponents/Displays/DisplayPrice/types'
import { OrderHeaderURLs } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { twinFetchPostJSON } from '../../../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { getPreparePricesConfigParam } from '../../../../../../../../../utils/reducers/getters'
import { parseOrderTableData } from '../../../../../../OrderQuoteLayout/function'
import { SetCompleteDataTy } from '../../../../../types'

interface ModalFinancialRowsOrderLogicProps {
    headerData: TwinDictionary
    setCompleteData: SetCompleteDataTy
    orderHeaderURLs: OrderHeaderURLs
    tableData?: OrderTableData | undefined
    financialRowTypesDict: TwinDictionary
}

const useModalFinancialRowsOrderLogic = ({ headerData, setCompleteData, orderHeaderURLs, tableData }: ModalFinancialRowsOrderLogicProps) => {
    const calculateParams = getPreparePricesConfigParam() as CalculateParamsTy
    const headerDataCalculate: HeaderDataCalculate = {
        OrderCosts: headerData?.OrderCosts,
        ParamsRoundingDecimals: calculateParams,
        OrderShipping: headerData?.OrderShipping,
        isFinished: headerData?.isFinished,
        OrderAccounts: headerData?.OrderAccounts
    }
    const parsedTableData = parseOrderTableData(tableData || [], headerDataCalculate)
    const myTotals = useMemo(() => {
        return {
            'total_after': roundNumberAndDecimals(parsedTableData.totals.subTotalAfterPrev - parsedTableData.totals.discounts_after_per_row, totalCalculateParams),
            'total_before': roundNumberAndDecimals(parsedTableData.totals.subtotal - parsedTableData.totals.discounts_before_per_row, totalCalculateParams)
        }
    }, [parsedTableData.totals.subTotalAfterPrev, parsedTableData.totals.discounts_after_per_row, parsedTableData.totals.subtotal, parsedTableData.totals.discounts_before_per_row])
    const [myTotalDiscounts, setMyTotalDiscounts] = useState<TwinDictionary>(calculateTotalsFnc(myTotals, headerData?.OrderFinancialRows || []))

    const recalculateTotals = useCallback((index: string, value: any) => {
        setMyTotalDiscounts(() => calculateTotalsFnc(myTotals, headerData?.OrderFinancialRows || [], index, value))
    }, [setMyTotalDiscounts, myTotals, headerData?.OrderFinancialRows])
    
    const handleChangeFinancialRows = useCallback((index: string, field: string, value: any) => {
        flushSync(async () => {
            let financialRow: TwinDictionary = {}
            let myFinancialRows: TwinDictionary[] = []
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                if (field === 'financialRowType') {
                    myData.header.OrderFinancialRows[index]['quantity'] = ''
                }
                if (field === 'discount' && myData.header.OrderFinancialRows[index]) {
                    myData.header.OrderFinancialRows[index]['quantity'] = value.discount ?? ''
                    myData.header.OrderFinancialRows[index]['calculationType'] = value.typeDiscount
                } else if (myData.header.OrderFinancialRows[index]) {
                    myData.header.OrderFinancialRows[index][field] = value
                }
                const type = parseInt(myData.header.OrderFinancialRows[index].financialRowType)
                if (type === financialRowTypesInversed['rechargeWithCosts'] || type === financialRowTypesInversed['rechargeWithoutCosts']) {
                    myData.header.OrderFinancialRows[index].beforeTax = true
                } else if (type === financialRowTypesInversed['rechargeFinancial']) {
                    myData.header.OrderFinancialRows[index].beforeTax = false
                }
                financialRow = myData.header.OrderFinancialRows[index]
                myFinancialRows = myData.header.OrderFinancialRows
                recalculateAllQuoteOrderRows(myData)
                return { ...myData }
            })
            setMyTotalDiscounts(() => calculateTotalsFnc(myTotals, myFinancialRows || [], index, value))
            await twinFetchPostJSON(orderHeaderURLs.updateFinancialRow, { ...financialRow })
        })
    }, [orderHeaderURLs.updateFinancialRow, setCompleteData, myTotals])

    const deleteRow = useCallback((index: string) => {
        flushSync(async () => {
            let financialRow: TwinDictionary = {}
            let myFinancialRows: TwinDictionary[] = []
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                financialRow = myData.header.OrderFinancialRows[index]
                myData.header.OrderFinancialRows.splice(index, 1)
                myFinancialRows = myData.header.OrderFinancialRows
                recalculateAllQuoteOrderRows(myData)
                return { ...myData }
            })
            await twinFetchPostJSON(orderHeaderURLs.deleteFinancialRow, {
                id: financialRow.id
            })
            setMyTotalDiscounts(() => calculateTotalsFnc(myTotals, myFinancialRows || []))
        })
    }, [orderHeaderURLs.deleteFinancialRow, setCompleteData, myTotals])

    const addTotalDiscountRow = useCallback((rowIndex: number) => {
        recalculateTotals(String(rowIndex), 0)
    }, [recalculateTotals])

    return { deleteRow, handleChangeFinancialRows, addTotalDiscountRow, myTotalDiscounts }
}

const calculateTotalsFnc = (totalsTotal: TwinDictionary, OrderFinancialRows: TwinDictionary[], index?: string, value?: any) => {
    const copyOld: TwinDictionary = {}
    const dictByDiscountsTotals: TwinDictionary = {}
    let totalDiscount = 0
    let totalField = value ? 'total_before' : 'total_after'
    for (const key in OrderFinancialRows) {
        const element = OrderFinancialRows[key]
        let discountQuantity = 0
        let myQuantity = element.quantity || 0
        let typeDiscount = element.calculationType
        totalField = element.beforeTax ? 'total_before' : 'total_after'
        if (index && String(key) === String(index)) {
            myQuantity = value.discount || 0
            typeDiscount = value.typeDiscount
        }
        if (parseInt(typeDiscount) === financialRowCalculationTypesInversed['quantity']) {
            discountQuantity = parseFloat(String(myQuantity))
        } else {
            discountQuantity = totalsTotal[totalField] * (parseFloat(String(myQuantity)) / 100)
        }
        dictByDiscountsTotals[key] = { discountQuantity, totalField, typeDiscount, cleanQuantity: parseFloat(myQuantity) }
        totalDiscount = totalDiscount + discountQuantity
    }
    totalDiscount = roundNumberAndDecimals(totalDiscount, totalCalculateParams)
    for (const key in dictByDiscountsTotals) {
        const element = dictByDiscountsTotals[key]
        if (parseInt(element.typeDiscount) === financialRowCalculationTypesInversed['quantity']) {
            copyOld[key] = roundNumberAndDecimals(totalsTotal[dictByDiscountsTotals[key]?.totalField] - (totalDiscount - dictByDiscountsTotals[key]?.discountQuantity), totalCalculateParams)
        } else {
            const myRest = roundNumberAndDecimals(totalsTotal[dictByDiscountsTotals[key]?.totalField] - (totalDiscount - dictByDiscountsTotals[key]?.discountQuantity), totalCalculateParams)
            const calcPercentage = roundNumberAndDecimals(myRest * 100 / totalsTotal[dictByDiscountsTotals[key]?.totalField], totalCalculateParams)
            copyOld[key] = calcPercentage
        }
    }
    return copyOld
}

export default useModalFinancialRowsOrderLogic